// Shared share-card data shaping (2026-08-18 lift, consolidation-reviewer
// finding): both hosts' PostSessionSummaryCard need the identical dateLabel
// formatting + i18n label mapping + sessionResultToShareCardData() call —
// this used to be two hand-copied memos (mobile + web-rnw) that could drift
// on the next stat-strip change. Same "2+ callsite → build the primitive"
// trigger useShareCardHandle.ts already followed for the capture+share step.
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  sessionResultToShareCardData,
  type SessionResult,
  type ShareCardData,
  type WorkoutEnergyReadout,
} from '@jackedlogs/shared';

export function useShareCardData(input: {
  result: SessionResult;
  socialHandle: string | null;
  unit: 'kg' | 'lb';
  locale: string;
  /** Resolved energy readout (calories + effort/HR); null when nothing
   *  estimable — the Calories/Effort stat cells are simply omitted. */
  energy?: WorkoutEnergyReadout | null;
  /** (slug, snapshotFallback) → localised display name — the caller's FK
   *  resolver, per [[feedback_no_denormalized_display_snapshots]]. */
  resolveName: (slug: string, snapshotFallback?: string) => string;
}): { shareData: ShareCardData; dateLabel: string } {
  const { result, socialHandle, unit, locale, energy = null, resolveName } = input;
  const { t } = useTranslation();

  // Pre-format the date here so the off-screen renderer stays locale-agnostic.
  const dateLabel = useMemo(() => {
    try {
      return new Intl.DateTimeFormat(locale, {
        weekday: 'short',
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      }).format(new Date(result.computedAt));
    } catch {
      return new Date(result.computedAt).toDateString();
    }
  }, [locale, result.computedAt]);

  const shareLabels = useMemo(
    () => ({
      durationMinute: t('mobile.share.durationMinute'),
      durationHour: t('mobile.share.durationHour'),
      durationMinuteShort: t('mobile.share.durationMinuteShort'),
      footerSets: t('mobile.share.footerSets'),
      footerDensitySuffix: t('mobile.share.footerDensitySuffix'),
      fallbackLabel: t('mobile.share.fallbackLabel'),
      timeLabel: t('mobile.share.timeLabel'),
      tutLabel: t('mobile.share.tutLabel'),
      impulseUnit: t('mobile.share.impulseUnit'),
      e1rmSuffix: t('mobile.share.e1rmSuffix'),
      // Stat-strip cell titles (HC-8 B3) — reuse the in-app postSession
      // block keys so no new i18n strings are minted.
      volumeLabel: t('mobile.postSession.blockVolume'),
      durationLabel: t('mobile.postSession.blockDuration'),
      densityLabel: t('mobile.postSession.blockDensity'),
      impulseLabel: t('mobile.share.impulseLabel'),
      caloriesLabel: t('mobile.postSession.blockCalories'),
      effortLabel: t('mobile.postSession.blockEffort'),
    }),
    [t],
  );

  const shareData = useMemo(
    () =>
      sessionResultToShareCardData({
        result,
        socialHandle,
        unit,
        locale,
        dateLabel,
        resolveName,
        labels: shareLabels,
        // Static image → the resolved canonical numbers, no (i) pill (B3).
        // Build the Effort string HERE (i18n in scope): "HRmax" localizes
        // (IT FCmax / DE HFmax / RU ЧСС), matching the on-screen block; "bpm"
        // is a universal unit, left inline.
        energy: energy
          ? {
              kcal: energy.resolved?.kcal ?? null,
              effortText:
                energy.hrMaxPct != null
                  ? t('mobile.postSession.effortHrMax', {
                      defaultValue: '{{pct}}% HRmax',
                      pct: energy.hrMaxPct,
                    })
                  : energy.avgHrBpm != null
                    ? `${energy.avgHrBpm} bpm`
                    : null,
            }
          : undefined,
      }),
    [result, socialHandle, unit, locale, dateLabel, resolveName, shareLabels, energy, t],
  );

  return { shareData, dateLabel };
}
